/**
 * CustomDomainCard (OCP-286) - the My-Instances surface for a tenant's
 * own vanity hostname on the Cloudflare-for-SaaS model.
 *
 * Lifecycle mirrors the backend (TenantCustomDomainService):
 *   not configured -> "Set custom domain" form
 *   pending/failed -> show the DNS records the customer must add at their
 *                     own provider + a re-check button
 *   active         -> verified badge + Remove
 *
 * Entitlement is enforced server-side; a non-entitled customer gets a
 * 403 on submit which renders as an upgrade hint.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api } from '../lib/api';
import { RoleReadOnlyNotice } from './RoleReadOnlyNotice';
import { usePortalAccess } from '../lib/use-portal-access';
import { fmtDate } from '../lib/format-time';

interface DnsInstruction {
  type: 'txt' | 'http' | 'cname';
  txt_name?: string;
  txt_value?: string;
  http_url?: string;
  http_body?: string;
}

interface CustomDomainState {
  configured: boolean;
  domain: string | null;
  status: 'pending' | 'active' | 'failed' | null;
  verified_at: string | null;
  cname_target: string;
  dns_instructions: DnsInstruction[];
  errors: string[];
}

/** Badge colours per status; the label is `instance-detail:customDomain.status.<key>` (OCP-656). */
const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
};

type Translate = (key: string, opts?: Record<string, unknown>) => string;

export function CustomDomainCard({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation('instance-detail');
  const { canManageInfra } = usePortalAccess();
  const [state, setState] = useState<CustomDomainState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const r = await api<CustomDomainState>(
          `/v1/portal/instances/${instanceId}/custom-domain`,
          { signal },
        );
        setState(r);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setLoadError(t('customDomain.loadFailed'));
      }
    },
    [instanceId, t],
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const onSet = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const domain = domainInput.trim();
      if (!domain) return;
      setBusy(true);
      setActionError(null);
      try {
        const r = await api<CustomDomainState>(
          `/v1/portal/instances/${instanceId}/custom-domain`,
          { method: 'POST', body: { domain } },
        );
        setState(r);
        setDomainInput('');
      } catch (err) {
        setActionError(messageForError(err, t));
      } finally {
        setBusy(false);
      }
    },
    [domainInput, instanceId, t],
  );

  const onRemove = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const r = await api<CustomDomainState>(
        `/v1/portal/instances/${instanceId}/custom-domain`,
        { method: 'DELETE' },
      );
      setState(r);
    } catch (err) {
      setActionError(messageForError(err, t));
    } finally {
      setBusy(false);
    }
  }, [instanceId, t]);

  const onRecheck = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      await load();
    } finally {
      setBusy(false);
    }
  }, [load]);

  return (
    <section className="mt-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('customDomain.title')}</h2>
        {state?.configured && state.status && (
          <span
            data-testid="custom-domain-status"
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              STATUS_BADGE[state.status] ?? 'bg-gray-200 text-gray-700'
            }`}
          >
            {state.status in STATUS_BADGE ? t(`customDomain.status.${state.status}`) : state.status}
          </span>
        )}
      </div>

      {loadError && (
        <p className="mt-2 text-sm text-red-700 dark:text-red-400">{loadError}</p>
      )}

      <RoleReadOnlyNotice scope="infra" className="mt-3" />
      {state && !state.configured && canManageInfra && (
        <form onSubmit={onSet} className="mt-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('customDomain.introBefore')}{' '}
            <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">kanban.acme.com</code>
            {t('customDomain.introAfter')}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              placeholder={t('customDomain.placeholder')}
              aria-label={t('customDomain.inputLabel')}
              className="min-w-[16rem] flex-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-1.5 text-sm"
            />
            <button
              type="submit"
              disabled={busy || domainInput.trim().length === 0}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {busy ? t('customDomain.setting') : t('customDomain.set')}
            </button>
          </div>
        </form>
      )}

      {state?.configured && (
        <div className="mt-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium">{state.domain}</span>
            {state.status === 'active' && state.verified_at && (
              <span className="text-gray-500">{t('customDomain.verified', { date: formatDate(state.verified_at) })}</span>
            )}
          </p>

          {(state.status === 'pending' || state.status === 'failed') && (
            <div className="mt-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('customDomain.dnsIntro')}
              </p>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full min-w-[32rem] text-left text-xs">
                  <thead className="text-gray-500">
                    <tr>
                      <th className="py-1 pr-3 font-medium">{t('customDomain.colType')}</th>
                      <th className="py-1 pr-3 font-medium">{t('customDomain.colName')}</th>
                      <th className="py-1 font-medium">{t('customDomain.colValue')}</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-gray-800 dark:text-gray-200">
                    {state.dns_instructions.map((d, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-800 align-top">
                        <td className="py-1 pr-3 uppercase">{d.type}</td>
                        <td className="py-1 pr-3 break-all">{d.txt_name ?? d.http_url ?? '-'}</td>
                        <td className="py-1 break-all">{d.txt_value ?? d.http_body ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {state.status === 'failed' && state.errors.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-xs text-red-700 dark:text-red-400">
                  {state.errors.map((er, i) => (
                    <li key={i}>{er}</li>
                  ))}
                </ul>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={onRecheck}
                  disabled={busy}
                  className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  {busy ? t('customDomain.checking') : t('customDomain.recheck')}
                </button>
                {canManageInfra && (
                <button
                  type="button"
                  onClick={onRemove}
                  disabled={busy}
                  className="rounded border border-red-300 dark:border-red-800 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  {t('customDomain.remove')}
                </button>
                )}
              </div>
            </div>
          )}

          {state.status === 'active' && canManageInfra && (
            <div className="mt-3">
              <button
                type="button"
                onClick={onRemove}
                disabled={busy}
                className="rounded border border-red-300 dark:border-red-800 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                {t('customDomain.removeDomain')}
              </button>
            </div>
          )}
        </div>
      )}

      {actionError && (
        <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-400">
          {actionError}
        </p>
      )}
    </section>
  );
}

function messageForError(err: unknown, t: Translate): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'feature_requires_paid_plan':
        return t('customDomain.error.paidPlan');
      case 'invalid_domain':
        return t('customDomain.error.invalidDomain');
      case 'domain_conflict':
        return t('customDomain.error.conflict');
      // OCP-870 - the instance is mid-deploy; nothing was changed.
      case 'deploy_in_flight':
        return t('customDomain.error.deployInFlight');
      default:
        return t('customDomain.error.http', { status: err.status });
    }
  }
  return t('customDomain.error.generic');
}

function formatDate(iso: string): string {
  return fmtDate(iso);
}
